'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'

import { Typography } from '@peaksandpies-paychex/ui/typography'
import c from 'clsx'

interface PaginationProps {
  pageCount: number
  currentPage?: number
  maxVisiblePages?: number
}

export const Pagination: React.FC<PaginationProps> = ({
  pageCount,
  currentPage = 1,
  maxVisiblePages = 7,
}) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [activeIndex, setActiveIndex] = useState<number>(currentPage)

  useEffect(() => {
    setActiveIndex(currentPage)
  }, [currentPage])

  const paginationRange = useMemo(() => {
    const totalPages = pageCount
    const currentPageIndex = activeIndex

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const leftSiblingIndex = Math.max(currentPageIndex - 1, 1)
    const rightSiblingIndex = Math.min(currentPageIndex + 1, totalPages)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1

    const firstPageIndex = 1
    const lastPageIndex = totalPages

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = Array.from({ length: 5 }, (_, i) => i + 1)
      return [...leftRange, '...', totalPages]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = Array.from({ length: 5 }, (_, i) => totalPages - 4 + i)
      return [firstPageIndex, '...', ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      )
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex]
    }

    return []
  }, [pageCount, activeIndex, maxVisiblePages])

  return (
    <nav
      className="flex w-full items-center justify-center gap-4"
      role="navigation"
      aria-label="Pagination"
    >
      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === '...') {
          return (
            <div
              key={`ellipsis-${index}`}
              className="flex size-8 items-center justify-center"
            >
              <Typography variant="P3" color="paychex-blue">
                ...
              </Typography>
            </div>
          )
        }

        // Construct canonical URL
        const params = new URLSearchParams(searchParams.toString())
        params.set('page', pageNumber.toString())
        const href = `${pathname}?${params.toString()}`

        return (
          <PaginationItem
            key={pageNumber}
            href={href}
            pageNumber={pageNumber as number}
            isCurrentIndex={activeIndex === pageNumber}
            onClick={() => {
              setActiveIndex(pageNumber as number)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )
      })}
    </nav>
  )
}

interface PaginationItemProps {
  href: string
  pageNumber: number
  isCurrentIndex: boolean
  onClick: () => void
}

const PaginationItem: React.FC<PaginationItemProps> = ({
  href,
  pageNumber,
  isCurrentIndex,
  onClick,
}) => {
  return (
    <Link
      href={href}
      scroll={false}
      className={c('flex size-8 items-center justify-center rounded-full', {
        'ease-in-out bg-blue-100 transition-colors duration-200':
          isCurrentIndex,
        'hover:bg-blue-10': !isCurrentIndex,
      })}
      aria-current={isCurrentIndex ? 'page' : undefined}
      onClick={onClick}
    >
      <Typography
        variant="P3"
        weight="bold"
        color={isCurrentIndex ? 'white' : 'paychex-blue'}
      >
        {pageNumber}
      </Typography>
    </Link>
  )
}
